// Session fan-out grouping — pure, DOM-free, unit-testable.
//
// When a work session's agent splits its job across subagents, the session body must
// show ONE collapsed row ("Split into 3 explorers · 2 done · 1 working") instead of N
// rows in the main flow, and the parent must not read as SILENT while it sits waiting
// on its children (a parent blocked on a fan-out writes nothing — that is exactly when
// it is busiest, and the ~2min "no signal" verdict would otherwise call it dead).
//
// Three upstream row shapes feed one summary, the first two mirroring what SubagentsPanel
// already renders on the agent view:
//   1. provider subagents — spawned BY the provider, streamed into providerSubagentStore
//      (plugins/agents/provider-subagents.ts). LIVE ONLY: a reload empties that store.
//   2. Paseo subagents — real Agent rows carrying `parentAgentId`.
//   3. persisted subagents — the subagent tool_calls already on the session's DURABLE
//      timeline, which is what makes the group survive a reload (see the PERSISTED half
//      further down).
// All three are normalized to SessionSubagentRow here so the session surface has ONE list
// and ONE status vocabulary. Display-name resolution stays in the component (it needs the
// cybo roster); this module takes the already-labelled structural input.

import type { ProviderSubagentStatus } from "./plugins/agents/provider-subagents.js";
import { headerLabel } from "./plugins/agents/tool-call-label.js";
import {
  countVisibleStreamEntries,
  type StreamEntryKind,
} from "./plugins/agents/stream-entry-visibility.js";

// The session feature's status vocabulary. No new words: a subagent reads the same way
// the rest of the session surfaces read.
export type SubagentState = "working" | "paused" | "done" | "failed";

export const SUBAGENT_STATE_LABEL: Record<SubagentState, string> = {
  working: "Working",
  paused: "Paused",
  done: "Done",
  failed: "Failed",
};

export interface SessionSubagentRow {
  // The agent-stream key this subagent's work is rendered under — the id AgentStreamView
  // is pointed at when the row is opened.
  id: string;
  label: string;
  // The agent TYPE when the label is the task description (CYBORG-1312), else null.
  detail: string | null;
  provider: string;
  state: SubagentState;
  // The provider-subagent status the reused SubagentConversation header speaks. Carried
  // so the drill-down needs no second mapping of its own (a Paseo row gets the nearest
  // equivalent of its lifecycle).
  providerStatus: ProviderSubagentStatus;
  // Whether this row's transcript can actually be opened. Absent = openable (every
  // caller that predates the persisted half). See isSubagentRowOpenable: a subagent
  // whose rows never reached this client — recovered from the DURABLE timeline after a
  // reload, or finished live having streamed nothing — has a row but no transcript to
  // attach to, and a row that opens onto a permanently empty body is worse than a row
  // that says so.
  openable?: boolean;
  // WHAT THE CHILD COST, carried so the session row renders the same time + tokens the agent
  // view's SubagentsPanel does (subagentRowMetrics resolves both). Null on a row recovered
  // from the durable timeline, which records the tool_call but not the child's usage —
  // absent, not zero, so the row shows the identity alone rather than a made-up "0 tokens".
  startedAt: number | null;
  finishedAt: number | null;
  tokens: number | null;
}

export interface SubagentFanout {
  rows: SessionSubagentRow[];
  total: number;
  working: number;
  paused: number;
  done: number;
  failed: number;
  // "explorers" / "subagents" — the plural used in the summary and the waiting line.
  noun: string;
  // "Split into 3 explorers · 2 done · 1 working".
  summary: string;
}

// A provider subagent's wire status → the session vocabulary. "canceled" is a FINISH,
// not a failure and not a pause (nothing resumes it), so it buckets with done.
export function subagentStateFromProvider(status: string): SubagentState {
  if (status === "running") return "working";
  if (status === "failed") return "failed";
  return "done";
}

// A Paseo Agent's lifecycle → the session vocabulary. Anything not running and not
// errored is an attached-but-idle child: paused.
export function subagentStateFromLifecycle(lifecycle: string | undefined | null): SubagentState {
  if (lifecycle === "running") return "working";
  if (lifecycle === "error") return "failed";
  return "paused";
}

// The provider-subagent status a SubagentState presents as, for the reused conversation
// header. Only used for Paseo rows (provider rows carry their real wire status).
export function providerStatusFor(state: SubagentState): ProviderSubagentStatus {
  if (state === "working") return "running";
  if (state === "failed") return "failed";
  if (state === "paused") return "canceled";
  return "completed";
}

export interface ProviderSubagentInput {
  id: string;
  label: string;
  detail?: string | null;
  provider: string;
  status: string;
  // Optional: satisfied structurally by the LIVE ProviderSubagentRow, absent on the
  // persisted half (a durable tool_call carries no usage and no completion clock).
  createdAt?: number | null;
  finishedAt?: number | null;
  tokens?: number | null;
}

export interface AgentSubagentInput {
  id: string;
  label: string;
  provider: string;
  lifecycle?: string | null;
}

// Normalize both upstream shapes into one ordered list (provider rows first, matching
// the order each source already hands us).
export function sessionSubagentRows(
  provider: readonly ProviderSubagentInput[],
  agents: readonly AgentSubagentInput[],
): SessionSubagentRow[] {
  const rows: SessionSubagentRow[] = provider.map((p) => ({
    id: p.id,
    label: p.label,
    detail: p.detail ?? null,
    provider: p.provider,
    state: subagentStateFromProvider(p.status),
    providerStatus: (p.status === "running" || p.status === "failed" || p.status === "canceled"
      ? p.status
      : "completed") as ProviderSubagentStatus,
    startedAt: p.createdAt ?? null,
    finishedAt: p.finishedAt ?? null,
    tokens: p.tokens ?? null,
  }));
  for (const a of agents) {
    const state = subagentStateFromLifecycle(a.lifecycle);
    rows.push({
      id: a.id,
      label: a.label,
      detail: null,
      provider: a.provider,
      state,
      providerStatus: providerStatusFor(state),
      // A Paseo subagent is a full Agent row with its own lifecycle, not a provider
      // descriptor — it reports neither a spawn clock nor usage on this surface.
      startedAt: null,
      finishedAt: null,
      tokens: null,
    });
  }
  return rows;
}

// Are these two normalized lists the SAME fan-out? The identity gate the session surface
// puts in front of everything derived from these rows.
//
// THE LAG the owner reported ("it lags while the subagent's number is being added up").
// A subagent's work reaches this client ONE ROW PER WIRE FRAME — app.svelte.ts's
// `provider_subagent` handler applies exactly one update per message — and each row calls
// AgentStreamState.touch(), which dirties every derived that reads that stream, including the per-row openability probe on the session surface. Five
// children of ~200 rows is ~1000 reactive frames for a group whose contents changed five
// times, and each one minted a fresh array, a fresh SubagentFanout (summarizeSubagentFanout
// allocates unconditionally) and a full SessionSubagentGroup re-render. RE-COMPUTING the
// rows is cheap; PROPAGATING them is not, and Svelte 5 stops propagation at a derived whose
// new value is `===` its previous one. So the surface hands the PREVIOUS array back whenever
// this says nothing changed, and the summary derived downstream does not even re-run.
//
// ponytail: every field of SessionSubagentRow spelled out rather than a generic deep
// compare — it is six strings and the list is short. A field added to the row type must
// be added here too, or the group stops updating on it.
export function sameSubagentRows(
  a: readonly SessionSubagentRow[],
  b: readonly SessionSubagentRow[],
): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  return a.every((row, i) => {
    const other = b[i];
    return (
      row.id === other.id &&
      row.label === other.label &&
      row.detail === other.detail &&
      row.provider === other.provider &&
      row.state === other.state &&
      row.providerStatus === other.providerStatus &&
      row.openable === other.openable &&
      row.startedAt === other.startedAt &&
      row.finishedAt === other.finishedAt &&
      // The LIVE token count. Leaving it out is exactly the "the group stops updating on it"
      // failure the note above warns about — the number would freeze at whatever it was when
      // some OTHER field last moved.
      row.tokens === other.tokens
    );
  });
}

// ── The PERSISTED half of the fan-out ───────────────────────────────────────────────
//
// providerSubagentStore is a LIVE, in-memory client store fed by the agent stream, so a
// full page reload empties it. Measured on a real session: while it ran, the body showed
// ONE collapsed row ("Split into 6 subagents · 6 done") that expanded into 6 openable rows;
// after a full reload "Split into" appeared nowhere and the SAME work re-rendered as 6
// separate raw tool cards. The body itself hydrated fine — only the grouping was gone.
//
// The durable timeline that body already hydrates still holds them: every provider subagent
// leaves ONE subagent tool_call on the parent's timeline whose `callId` IS the subagent id
// (the sidechain tracker keys the descriptor, the tool card and the timeline relay on the
// same parentToolUseId). So the group is rebuildable from the page already in hand — no new
// fetch, no new store, and normalized through sessionSubagentRows like the live half, so
// there is still ONE status vocabulary and ONE list.

// The structural slice of a hydrated stream entry this reads (AgentStreamState.StreamEntry).
// Deliberately loose so this module stays free of the store's types: `detail` is narrowed
// here, and a non-tool entry simply doesn't match.
export interface TimelineToolRow {
  kind: string;
  callId?: string;
  name?: string;
  status?: string;
  detail?: unknown;
}

// The provider tools that spawn a subagent — same set the tool-card header uses.
const SUBAGENT_TOOL_NAMES: ReadonlySet<string> = new Set([
  "Task",
  "Agent",
  "Workflow",
  "Sub-agent",
]);

function readSubAgentDetail(
  detail: unknown,
): { subAgentType?: string; description?: string } | null {
  if (typeof detail !== "object" || detail === null) return null;
  const record = detail as { type?: unknown; subAgentType?: unknown; description?: unknown };
  if (record.type !== "sub_agent") return null;
  return {
    ...(typeof record.subAgentType === "string" ? { subAgentType: record.subAgentType } : {}),
    ...(typeof record.description === "string" ? { description: record.description } : {}),
  };
}

// Durable subagent tool_calls → the SAME input shape the live provider store yields.
// `provider` comes from the session's cybo (a durable row carries no provider of its own);
// it only drives the row's glyph. The label mirrors the live descriptor's resolution
// (title ?? description, provider-subagents.ts): the subagent TYPE first — one word, so the
// group summary still reads "Split into 6 explorers" — then the task description, both
// through the header-shape guard so no provider envelope can reach this label either.
//
// ── SCOPED TO THE CURRENT TURN, and why ──────────────────────────────────────────────
//
// MEASURED ON THE OWNER'S DAEMON. He sent a message at 17:17:17 into a session whose agent
// was mid-turn (it queued, which is how he knew it was busy). The agent recorded ZERO
// subagent spawns after that message — and within half a second the UI drew FIVE rows
// reading "Subagent · Working" directly underneath it. Five children that did not exist,
// attributed to a prompt that had not run yet.
//
// Nothing spawned them: they were an EARLIER turn's children. This function used to scan
// the session's WHOLE timeline and hand back one flat list, and the group that list feeds
// is rendered in the live footer — the tail of the stream, i.e. under the newest message.
// So every fan-out the session ever had was re-anchored, forever, to whatever was said
// last. The anchoring was lost HERE, in the derivation, not in the component that draws it:
// a fan-out belongs to the turn that spawned it, and the turn a footer row can honestly
// describe is the current one. Earlier turns keep their subagent tool cards in the
// transcript, in place, exactly where they happened.
//
// Second wrong, same measurement: those rows still read "Working". A subagent tool_call
// whose durable row never received its terminal status stays "running" on the timeline
// forever, and the orphan sweep that would fix it only runs once the SESSION settles
// (SessionWorkView's hydrate passes `settled`), not when a turn ends. So a `turn_boundary`
// demotes every still-"running" child collected before it: that turn is over, and nothing
// that ran inside it is still running. This is the UI half only — a live provider
// descriptor that the daemon never reports as finished is the daemon's to fix.
export function timelineSubagentInputs(
  rows: readonly TimelineToolRow[],
  provider: string,
): ProviderSubagentInput[] {
  // The CURRENT turn starts at the newest prompt. A session with no prompt at all (an
  // autonomous run, a scheduled wake) has exactly one turn, so the whole page is it.
  let start = 0;
  for (let i = rows.length - 1; i >= 0; i--) {
    if (rows[i].kind === "user_message") {
      start = i;
      break;
    }
  }
  const inputs: ProviderSubagentInput[] = [];
  const seen = new Set<string>();
  for (let i = start; i < rows.length; i++) {
    const row = rows[i];
    if (row.kind === "turn_boundary") {
      // The turn these children ran in is over — a "running" one is a stale durable row,
      // not live work.
      for (const input of inputs) {
        if (input.status === "running") input.status = "completed";
      }
      continue;
    }
    if (row.kind !== "tool_call" || !row.callId || !row.name) continue;
    if (!SUBAGENT_TOOL_NAMES.has(row.name)) continue;
    const detail = readSubAgentDetail(row.detail);
    if (!detail) continue;
    // One entry per callId: the timeline folds a call's rows in place, but a page stitched
    // from several fetches could still hand us the same call twice.
    if (seen.has(row.callId)) continue;
    seen.add(row.callId);
    // The spec: the task description names the card, the subagent type is the fallback (and the second line).
    const type = headerLabel(detail.subAgentType);
    const label = headerLabel(detail.description) ?? type ?? "Subagent";
    inputs.push({
      id: row.callId,
      label,
      // Only when it is not already the label: the same words twice on one row is noise.
      detail: type !== null && type !== label ? type : null,
      provider,
      status: row.status ?? "completed",
    });
  }
  return inputs;
}

// Merge the LIVE provider rows with the persisted ones. Live WINS on a collision: the same
// subagent is both streaming now and already committed to the timeline, and the store holds
// the fresher status. One subagent is always ONE row, never two.
export function mergeSubagentInputs(
  live: readonly ProviderSubagentInput[],
  persisted: readonly ProviderSubagentInput[],
): ProviderSubagentInput[] {
  const known = new Set(live.map((row) => row.id));
  return [...live, ...persisted.filter((row) => !known.has(row.id))];
}

// Shown on a row whose transcript is gone. English, user-facing, and honest.
//
// Deliberately NOT "not kept after a reload" any more: that named only ONE of the two
// ways a row ends up with nothing behind it, and the OTHER one was measured live, in a
// single page session with no reload at all (see isSubagentRowOpenable below). Copy
// that explains a cause it can't actually know is worse than copy that states the fact.
export const SUBAGENT_TRANSCRIPT_UNAVAILABLE = "Transcript not available";

// Can this row's transcript actually be OPENED?
//
// The drill-down (SubagentConversation → AgentStreamView) renders from the client's
// in-memory AgentStreamState, which a full reload empties. So after a reload a FINISHED
// subagent has a row and, until something re-fills the stream, nothing behind it.
//
// What re-fills it is subagentsNeedingTranscriptBackfill below → the
// cyborg:fetch_provider_subagent_timeline read, now backed by a DURABLE store: the relay
// mirrors the `timeline` sub-event into provider_subagent_timeline_rows (migration 0142),
// so the transcript survives the owning daemon's process. This gate still holds, because
// the fetch is asynchronous and a row is inert until its rows land — and because a
// subagent that genuinely recorded nothing has nothing to fetch, and a row that opens
// onto a permanently empty body is worse than a row that says so.
//
// A still-WORKING subagent is different: its daemon keeps streaming into the same store, so
// its rows arrive on their own and the drill-down fills in.
//
// `transcript` is the entry array the client holds for the subagent id — THE SAME ARRAY
// the drill-down renders, counted THE SAME WAY (countVisibleStreamEntries). Taking the
// array instead of a number is the fix for the live defect: this used to be handed
// `getEntries(id).length`, and a finished provider subagent that never streamed a single
// row still holds ONE entry — the turn_boundary synthesized from its running -> completed
// edge. A raw length said "1 row, openable"; the drill-down then rendered that one entry
// as a hairline and, being non-empty, skipped its own empty state too. The row opened
// onto blank white space with no explanation. Counting here, from the array, is what
// makes the gate and the view structurally incapable of disagreeing.
export function isSubagentRowOpenable(
  row: SessionSubagentRow,
  transcript: readonly StreamEntryKind[],
): boolean {
  return countVisibleStreamEntries(transcript) > 0 || row.state === "working";
}

// Should the fan-out GROUP fetch this subagent's transcript in the background?
//
// When this client holds nothing visible for it: a finished child whose rows never
// arrived, or a running one whose body is still empty. Counted with the same
// countVisibleStreamEntries as the openable gate and the drill-down's own empty state,
// so all three agree on "is there anything here". A throttle, not a dedupe: the merge
// (providerSubagentStore.mergeTranscript) dedupes by seq, and opening the viewer always
// loads the whole transcript.
export function shouldBackfillSubagentTranscript(transcript: readonly StreamEntryKind[]): boolean {
  return countVisibleStreamEntries(transcript) === 0;
}

// Which of a fan-out's rows must be fetched — and WHY this is a list over the whole
// group instead of a single id read off the open drill-down.
//
// THE DEADLOCK. Both gates above are individually correct, and wiring the fetch to the
// OPENED conversation made them lock each other shut. Measured against relay-v0.0.408
// on a reloaded session: "Split into 9 subagents · 9 done", 9 rows, 0 openable, and no
// request on the wire at all. A finished subagent recovered from the durable timeline
// holds only its synthesized completion boundary, so:
//     isSubagentRowOpenable        -> false  (0 visible entries, state !== "working")
//   → the row renders `disabled`, the click is a no-op, nothing ever opens
//   → the drill-down never mounts, so the effect keyed on the open row never runs
//     shouldBackfillSubagentTranscript -> true, forever, and never consulted.
// No content ⇒ not openable ⇒ never fetched ⇒ never content. The honest empty state
// was right; the row simply had no way left to ask for anything.
//
// So the fetch is driven by the GROUP rendering — something a person actually reaches by
// expanding the fan-out — not by the conversation they cannot open yet. Openability is
// deliberately NOT consulted here: the rows that need this most are exactly the inert
// ones. Once the rows land, isSubagentRowOpenable flips true on its own and the row
// becomes clickable.
export function subagentsNeedingTranscriptBackfill(
  rows: readonly SessionSubagentRow[],
  transcriptFor: (subagentId: string) => readonly StreamEntryKind[],
): string[] {
  return rows.filter((r) => shouldBackfillSubagentTranscript(transcriptFor(r.id))).map((r) => r.id);
}

// The group noun. When every child carries the SAME single-word label ("explorer") that
// word pluralizes and the summary reads "Split into 3 explorers". A sentence-y title
// ("Explore the auth flow") would read as nonsense pluralized, and mixed labels have no
// common noun at all — both fall back to the neutral "subagents".
export function fanoutNoun(rows: readonly SessionSubagentRow[]): string {
  const plural = rows.length !== 1;
  // The noun is the agent TYPE: the row's detail when the label is its description, else the label itself.
  const labels = new Set(rows.map((r) => (r.detail ?? r.label).trim().toLowerCase()));
  const only = labels.size === 1 ? [...labels][0] : null;
  if (!only || !/^[a-z][a-z-]*$/.test(only)) return plural ? "subagents" : "subagent";
  if (!plural) return only;
  return only.endsWith("s") ? only : `${only}s`;
}

// Collapse N subagent rows into ONE summary. Returns null when there are no subagents —
// the control case: a session that never fanned out renders exactly as it does today,
// with no group row at all.
export function summarizeSubagentFanout(
  rows: readonly SessionSubagentRow[],
): SubagentFanout | null {
  if (rows.length === 0) return null;
  const count = (s: SubagentState) => rows.filter((r) => r.state === s).length;
  const working = count("working");
  const paused = count("paused");
  const done = count("done");
  const failed = count("failed");
  const noun = fanoutNoun(rows);
  const parts = [`Split into ${rows.length} ${noun}`];
  if (done > 0) parts.push(`${done} done`);
  if (working > 0) parts.push(`${working} working`);
  if (failed > 0) parts.push(`${failed} failed`);
  if (paused > 0) parts.push(`${paused} paused`);
  return {
    rows: [...rows],
    total: rows.length,
    working,
    paused,
    done,
    failed,
    noun,
    summary: parts.join(" · "),
  };
}

// Which subagent is open on a session surface, KEYED BY SESSION.
//
// The key is the whole point. The first version held the open row in a bare `$state` and
// cleared it from an `$effect` that depended on `session.id` ("a session switch must never
// leave someone inside a child of the PREVIOUS session"). But the session surface is handed
// a BRAND-NEW session object on every live broadcast — applyWorkSession replaces the row
// even for a no-op echo, including the one emitted by merely opening/navigating to the
// session — so that effect re-ran and nulled the selection milliseconds after the click.
// Clicking a subagent row appeared to do nothing.
//
// Deriving the open row from a session-keyed selection makes the session's IDENTITY
// irrelevant and only its ID matter: a re-render can't close the drill-down, while a real
// session switch still drops it with no effect involved.
export interface OpenSubagentSelection {
  sessionId: string;
  row: SessionSubagentRow;
}

export function openSubagentFor(
  selection: OpenSubagentSelection | null,
  sessionId: string,
): SessionSubagentRow | null {
  return selection && selection.sessionId === sessionId ? selection.row : null;
}

// Is the parent idle ONLY because its children are still running? This is the liveness
// bridge: the parent writes nothing while it waits on a fan-out, so every "is this
// session silent" verdict must consult this before calling it dead.
export function isWaitingOnSubagents(
  parentWorking: boolean,
  fanout: SubagentFanout | null,
): boolean {
  return !parentWorking && (fanout?.working ?? 0) > 0;
}

// The parent's liveness INCLUDING its children — true whenever the session is doing
// work, whether the parent itself is mid-turn or its subagents are.
export function sessionIsBusy(parentWorking: boolean, fanout: SubagentFanout | null): boolean {
  return parentWorking || isWaitingOnSubagents(parentWorking, fanout);
}

// "waiting on 3 explorers" — the honest line for a parent that is blocked on its
// children. Null when it isn't waiting on anything.
export function waitingOnSubagentsLabel(fanout: SubagentFanout | null): string | null {
  if (!fanout || fanout.working === 0) return null;
  // Noun over the WORKING rows only, so "waiting on 1 explorer" stays singular and a
  // mixed-label fan-out with one live child still reads right.
  const noun = fanoutNoun(fanout.rows.filter((r) => r.state === "working"));
  return `waiting on ${fanout.working} ${noun}`;
}
